/**
 * API 클라이언트 유틸리티
 * 백엔드 서버와의 통신을 위한 인증 토큰 관리 및 HTTP 요청 래퍼
 */

// 백엔드 API 기본 URL (브라우저: 상대경로 /api, SSR: localhost 직접 연결)
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ||
  (typeof window !== 'undefined' ? '/api' : 'http://localhost:4000/api')

/** API 응답 공통 타입 */
export interface ApiResponse<T = unknown> {
  success: boolean
  data: T
  message?: string
  error?: string
}

/** 페이지네이션 응답 타입 */
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  totalPages: number
  limit: number
}

/** API 에러 클래스 */
export class ApiError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * 로컬 스토리지에서 인증 토큰 가져오기
 * zustand persist 스토어의 구조에 맞게 파싱
 */
function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem('video-editor-auth')
    if (!stored) return null

    const parsed = JSON.parse(stored)
    return parsed?.state?.token || null
  } catch {
    return null
  }
}

/**
 * 로컬 스토리지에서 리프레시 토큰 가져오기
 */
function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem('video-editor-auth')
    if (!stored) return null

    const parsed = JSON.parse(stored)
    return parsed?.state?.refreshToken || null
  } catch {
    return null
  }
}

/**
 * 로컬 스토리지의 토큰 업데이트 (Zustand persist 구조 유지)
 */
function updateStoredTokens(token: string, refreshToken: string): void {
  if (typeof window === 'undefined') return

  try {
    const stored = localStorage.getItem('video-editor-auth')
    if (!stored) return

    const parsed = JSON.parse(stored)
    if (parsed?.state) {
      parsed.state.token = token
      parsed.state.refreshToken = refreshToken
      localStorage.setItem('video-editor-auth', JSON.stringify(parsed))
    }
  } catch {
    // 저장 실패 시 무시
  }
}

/** 토큰 갱신 진행 중 플래그 (중복 갱신 방지) */
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

/**
 * 리프레시 토큰으로 액세스 토큰 갱신
 * 중복 호출 방지를 위해 진행 중인 갱신이 있으면 동일 Promise 반환
 */
async function refreshAccessToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken()
      if (!refreshToken) return false

      const url = `${API_BASE_URL}/auth/refresh`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) return false

      const data = await response.json()
      if (data.token && data.refreshToken) {
        updateStoredTokens(data.token, data.refreshToken)
        return true
      }
      return false
    } catch {
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * 공통 HTTP 요청 함수
 * 자동으로 인증 헤더 추가 및 에러 처리
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken()
  const url = `${API_BASE_URL}${endpoint}`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  }

  // 인증 토큰이 있으면 Authorization 헤더 추가
  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  // FormData 전송 시 Content-Type 제거 (브라우저가 자동 설정)
  if (options.body instanceof FormData) {
    delete (headers as Record<string, string>)['Content-Type']
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // 204 No Content 응답 처리
  if (response.status === 204) {
    return {} as T
  }

  // 응답 본문 파싱
  let data: ApiResponse<T>
  try {
    data = await response.json()
  } catch {
    throw new ApiError('서버 응답을 파싱할 수 없습니다.', response.status)
  }

  // 에러 응답 처리
  if (!response.ok) {
    // 인증 만료 시 토큰 갱신 시도 후 재요청
    if (response.status === 401) {
      const refreshed = await refreshAccessToken()
      if (refreshed) {
        // 갱신 성공 → 새 토큰으로 재요청
        const newToken = getAuthToken()
        if (newToken) {
          ;(headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`
        }
        const retryResponse = await fetch(url, { ...options, headers })

        if (retryResponse.status === 204) return {} as T

        let retryData: ApiResponse<T>
        try {
          retryData = await retryResponse.json()
        } catch {
          throw new ApiError('서버 응답을 파싱할 수 없습니다.', retryResponse.status)
        }

        if (!retryResponse.ok) {
          // 재시도도 실패 → 로그아웃
          if (typeof window !== 'undefined') {
            localStorage.removeItem('video-editor-auth')
            window.location.href = '/login'
          }
          throw new ApiError('인증이 만료되었습니다. 다시 로그인해주세요.', 401)
        }

        const retryRaw = retryData as unknown as Record<string, unknown>
        return retryRaw.data !== undefined ? retryRaw.data as T : retryData as unknown as T
      }

      // 갱신 실패 → 로그아웃
      if (typeof window !== 'undefined') {
        localStorage.removeItem('video-editor-auth')
        window.location.href = '/login'
      }
      throw new ApiError('인증이 만료되었습니다. 다시 로그인해주세요.', 401)
    }
    const errorMessage = data?.error || data?.message || `요청 실패 (${response.status})`
    throw new ApiError(errorMessage, response.status, data?.error)
  }

  // 백엔드가 { success, data } 래퍼를 사용하면 data.data, 아니면 직접 반환
  const raw = data as unknown as Record<string, unknown>
  return raw.data !== undefined
    ? raw.data as T
    : data as unknown as T
}

/** GET 요청 */
export async function get<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET' })
}

/** POST 요청 */
export async function post<T>(endpoint: string, body?: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/** PUT 요청 */
export async function put<T>(endpoint: string, body?: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/** PATCH 요청 */
export async function patch<T>(endpoint: string, body?: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/** DELETE 요청 */
export async function del<T = void>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE' })
}

/** 파일 업로드 (FormData) */
export async function upload<T>(endpoint: string, formData: FormData): Promise<T> {
  const token = getAuthToken()
  const url = `${API_BASE_URL}${endpoint}`

  const headers: HeadersInit = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  })

  let data: ApiResponse<T>
  try {
    data = await response.json()
  } catch {
    throw new ApiError('파일 업로드 응답을 파싱할 수 없습니다.', response.status)
  }

  if (!response.ok) {
    throw new ApiError(
      data?.error || '파일 업로드에 실패했습니다.',
      response.status
    )
  }

  const raw = data as unknown as Record<string, unknown>
  return raw.data !== undefined
    ? raw.data as T
    : data as unknown as T
}

// ==================== Makers API ====================

/** YouTube 하이라이트 프로젝트 생성 */
export function createYouTubeHighlight<T>(config: unknown): Promise<T> {
  return post<T>('/makers/youtube-highlight', config)
}

/** 롱폼 자동 생성 프로젝트 생성 */
export function createLongform<T>(config: unknown): Promise<T> {
  return post<T>('/makers/longform', config)
}

/** 숏폼 자동 생성 프로젝트 생성 */
export function createShortform<T>(config: unknown): Promise<T> {
  return post<T>('/makers/shortform', config)
}

/** 무협 스튜디오 프로젝트 생성 */
export function createWuxia<T>(config: unknown): Promise<T> {
  return post<T>('/makers/wuxia', config)
}

/** 메이커 템플릿 목록 조회 */
export function fetchMakerTemplates<T>(type?: string): Promise<T> {
  const query = type ? `?type=${encodeURIComponent(type)}` : ''
  return get<T>(`/makers/templates${query}`)
}

// ==================== Pipelines API ====================

/** 파이프라인 상세 조회 */
export function fetchPipeline<T>(id: string): Promise<T> {
  return get<T>(`/pipelines/${id}`)
}

/** 파이프라인 재시도 */
export function retryPipeline<T>(id: string): Promise<T> {
  return post<T>(`/pipelines/${id}/retry`)
}

/** 파이프라인 취소 */
export function cancelPipeline<T>(id: string): Promise<T> {
  return post<T>(`/pipelines/${id}/cancel`)
}

/** 파이프라인 다음 단계 진행 */
export function proceedPipeline<T>(id: string): Promise<T> {
  return post<T>(`/pipelines/${id}/proceed`)
}

/** 파이프라인 단계 데이터 수정 */
export function updatePipelineStep<T>(id: string, stepName: string, data: unknown): Promise<T> {
  return patch<T>(`/pipelines/${id}/steps/${stepName}`, data)
}

/** 파이프라인 자산 한 장 다시 만들기 (캐릭터·배경·장면) */
export function regeneratePipelineAsset<T>(id: string, body: { kind: 'character' | 'location' | 'scene'; id: string; prompt?: string }): Promise<T> {
  return post<T>(`/pipelines/${id}/regenerate`, body)
}

/** 완료된 파이프라인 결과물을 서버로 올리기 (맥 → 운영 서버) */
export function publishPipeline<T>(id: string): Promise<T> {
  return post<T>(`/pipelines/${id}/publish`)
}

// ==================== Trends API ====================

/** 인기 영상 조회 */
export function fetchTrendingVideos<T>(params?: { regionCode?: string; categoryId?: string; maxResults?: number }): Promise<T> {
  const searchParams = new URLSearchParams()
  if (params?.regionCode) searchParams.set('regionCode', params.regionCode)
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId)
  if (params?.maxResults) searchParams.set('maxResults', String(params.maxResults))
  const query = searchParams.toString()
  return get<T>(`/trends/popular${query ? `?${query}` : ''}`)
}

/** 키워드 검색 */
export function searchTrendVideos<T>(keyword: string, params?: { regionCode?: string; order?: string; maxResults?: number }): Promise<T> {
  const searchParams = new URLSearchParams({ keyword })
  if (params?.regionCode) searchParams.set('regionCode', params.regionCode)
  if (params?.order) searchParams.set('order', params.order)
  if (params?.maxResults) searchParams.set('maxResults', String(params.maxResults))
  return get<T>(`/trends/search?${searchParams}`)
}

/** Google Trends 키워드 분석 */
export function fetchKeywordTrend<T>(keyword: string, params?: { geo?: string; timeRange?: string }): Promise<T> {
  const searchParams = new URLSearchParams({ keyword })
  if (params?.geo) searchParams.set('geo', params.geo)
  if (params?.timeRange) searchParams.set('timeRange', params.timeRange)
  return get<T>(`/trends/keyword?${searchParams}`)
}

/** 채널 벤치마킹 분석 */
export function fetchChannelStats<T>(channelId: string): Promise<T> {
  return get<T>(`/trends/channel/${encodeURIComponent(channelId)}`)
}

/** AI 기반 주제 추천 */
export function fetchTopicSuggestions<T>(niche: string, params?: { platform?: string; count?: number }): Promise<T> {
  const searchParams = new URLSearchParams({ niche })
  if (params?.platform) searchParams.set('platform', params.platform)
  if (params?.count) searchParams.set('count', String(params.count))
  return get<T>(`/trends/suggestions?${searchParams}`)
}

/** API 클라이언트 인스턴스 (네임스페이스 방식 내보내기) */
const api = {
  get, post, put, patch, del, upload,
  // Makers
  createYouTubeHighlight, createLongform, createShortform, createWuxia, fetchMakerTemplates,
  // Pipelines
  fetchPipeline, retryPipeline, cancelPipeline, proceedPipeline, updatePipelineStep, regeneratePipelineAsset, publishPipeline,
  // Trends
  fetchTrendingVideos, searchTrendVideos, fetchKeywordTrend, fetchChannelStats, fetchTopicSuggestions,
}
export default api

// ==================== AI 유튜브 공장 (제목 큐) ====================

/** 제목 큐 목록 (당일 API 지출 포함) */
export function fetchTitleQueue<T>(params?: { status?: string; seriesId?: string; limit?: number }): Promise<T> {
  const sp = new URLSearchParams()
  if (params?.status) sp.set('status', params.status)
  if (params?.seriesId) sp.set('seriesId', params.seriesId)
  if (params?.limit) sp.set('limit', String(params.limit))
  const qs = sp.toString()
  return get<T>(`/titles${qs ? `?${qs}` : ''}`)
}

/** 제목 큐 상세 (바이블·클립·실원가·재생성률) */
export function fetchTitleDetail<T>(id: string): Promise<T> {
  return get<T>(`/titles/${id}`)
}

/** 제목 리스트 일괄 등록 — 붙여넣기 텍스트 또는 배열 */
export function uploadTitleList<T>(payload: {
  text?: string
  titles?: string[]
  genre?: string
  seriesId?: string
  targetDuration?: number
}): Promise<T> {
  return post<T>('/titles/bulk', payload)
}

/** 제목 1건 실행 */
export function runTitle<T>(id: string): Promise<T> {
  return post<T>(`/titles/${id}/run`)
}

/** PENDING 건 일괄 실행 */
export function runTitleBatch<T>(limit = 5): Promise<T> {
  return post<T>('/titles/run-batch', { limit })
}

/** 실패 건 재시도 — failedStep 부터만 다시 돈다 */
export function retryTitle<T>(id: string): Promise<T> {
  return post<T>(`/titles/${id}/retry`)
}

/** 제목 큐 삭제 */
export function deleteTitle<T = void>(id: string): Promise<T> {
  return del<T>(`/titles/${id}`)
}

// ==================== YouTube ====================

/** YouTube 연동 상태 */
export function fetchYouTubeStatus<T>(): Promise<T> {
  return get<T>('/youtube/status')
}

/** OAuth 동의 화면 URL */
export function fetchYouTubeAuthUrl<T>(): Promise<T> {
  return get<T>('/youtube/oauth/url')
}

/** 발행 승인 대기 목록 */
export function fetchApprovals<T>(status = 'waiting'): Promise<T> {
  return get<T>(`/youtube/approvals?status=${encodeURIComponent(status)}`)
}

/** 발행 승인 — 썸네일·제목 선택 후 공개/예약 */
export function approvePublish<T>(
  id: string,
  payload: { selectedTitle?: string; selectedThumbnail?: string; publishAt?: string }
): Promise<T> {
  return post<T>(`/youtube/approvals/${id}/approve`, payload)
}

/** 발행 거부 */
export function rejectPublish<T>(id: string): Promise<T> {
  return post<T>(`/youtube/approvals/${id}/reject`)
}

/** 성과 조회 — APV/VPH 시계열 + 판정 + 제목 패턴별 비교 */
export function fetchPerformance<T>(videoId?: string): Promise<T> {
  return get<T>(`/youtube/performance${videoId ? `?videoId=${encodeURIComponent(videoId)}` : ''}`)
}

/** 성과 수집 트리거 */
export function collectPerformance<T>(youtubeVideoId: string, titleQueueId?: string): Promise<T> {
  return post<T>('/youtube/performance/collect', { youtubeVideoId, titleQueueId })
}

/** 클립 재생성 — 해당 컷 캐시를 지우고 파이프라인을 재시도한다 */
export function regenerateClip<T>(titleQueueId: string): Promise<T> {
  return post<T>(`/titles/${titleQueueId}/retry`)
}
